import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Phone as PhoneIcon,
  LocalShipping as ShippingIcon,
  Inventory as MaterialIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  CheckCircleOutline as FinalizeIcon,
  History as HistoryIcon,
  Today as TodayIcon,
  CompareArrows as CompareIcon,
  EmojiEvents as TrophyIcon,
  StarOutline as StarIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { getTodayBrasilia, formatDateBR } from '../utils/dateUtils';

interface Sale {
  id: string;
  sellerId: string;
  clientPhone: string;
  freightValue: number;
  materialValue: number;
  totalValue: number;
  saleDate: string;
  isFirstSale: boolean;
  observations?: string;
  seller: {
    id: string;
    name: string;
    email: string;
  };
}

interface NewSale {
  clientPhone: string;
  freightValue: string;
  materialValue: string;
  saleDate: string;
  isFirstSale: string;
  observations: string;
}

interface SellerStats {
  daily: {
    total: number;
    count: number;
    status: string;
    message: string;
    color: string;
    icon: string;
  };
  monthly: {
    total: number;
    count: number;
    progress: number;
    goal: number;
    remaining: number;
    bonus?: number;
  };
  comparison: {
    percentage: number;
    lastMonthTotal: number;
    trend: string;
  };
}

interface DailyHistory {
  id: string;
  sellerId: string;
  date: string;
  totalSales: number;
  salesCount: number;
  goalStatus: string;
  goalMessage: string;
  createdAt: string;
}

interface MonthlyStats {
  month: number;
  year: number;
  monthName: string;
  totalSales: number;
  salesCount: number;
  avgDailySales: number;
  bestDay: { date: string; total: number } | null;
  worstDay: { date: string; total: number } | null;
  daysWithGoodGoal: number;
  daysWithBasicGoal: number;
  daysWithBelowGoal: number;
}

const SellerSales: React.FC = () => {
  const { user } = useAuth();
  const [sales, setSales] = useState<Sale[]>([]);
  const [stats, setStats] = useState<SellerStats | null>(null);
  const [history, setHistory] = useState<DailyHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>(getTodayBrasilia());
  const [currentTab, setCurrentTab] = useState(0);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openFinalizeDialog, setOpenFinalizeDialog] = useState(false);
  const [editingSale, setEditingSale] = useState<Sale | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  const [newSale, setNewSale] = useState<NewSale>({
    clientPhone: '',
    freightValue: '',
    materialValue: '',
    saleDate: '',
    isFirstSale: '',
    observations: '',
  });

  // Estados para filtro mensal do histórico
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats | null>(null);
  const [previousMonthStats, setPreviousMonthStats] = useState<MonthlyStats | null>(null);
  const [filteredHistory, setFilteredHistory] = useState<DailyHistory[]>([]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Nomes dos meses
  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // Calcular estatísticas mensais a partir do histórico
  const calculateMonthlyStats = React.useCallback((historyData: DailyHistory[], month: number, year: number): MonthlyStats | null => {
    const filtered = historyData.filter(item => {
      const date = new Date(item.date);
      return date.getMonth() + 1 === month && date.getFullYear() === year;
    });

    if (filtered.length === 0) return null;

    const totalSales = filtered.reduce((sum, item) => sum + Number(item.totalSales), 0);
    const salesCount = filtered.reduce((sum, item) => sum + item.salesCount, 0);
    const avgDailySales = totalSales / filtered.length;

    const sortedByTotal = [...filtered].sort((a, b) => Number(b.totalSales) - Number(a.totalSales));
    const bestDay = sortedByTotal[0] ? { date: sortedByTotal[0].date, total: Number(sortedByTotal[0].totalSales) } : null;
    const worstDay = sortedByTotal[sortedByTotal.length - 1] ? { date: sortedByTotal[sortedByTotal.length - 1].date, total: Number(sortedByTotal[sortedByTotal.length - 1].totalSales) } : null;

    return {
      month,
      year,
      monthName: monthNames[month - 1],
      totalSales,
      salesCount,
      avgDailySales,
      bestDay,
      worstDay,
      daysWithGoodGoal: filtered.filter(h => h.goalStatus === 'GOOD').length,
      daysWithBasicGoal: filtered.filter(h => h.goalStatus === 'BASIC').length,
      daysWithBelowGoal: filtered.filter(h => h.goalStatus === 'BELOW_MINIMUM').length,
    };
  }, [monthNames]);

  // Função para carregar histórico do mês selecionado e mês anterior
  const loadHistoryForMonth = React.useCallback(async (month: number, year: number) => {
    if (!user) return;
    
    try {
      // Calcular mês anterior para comparação
      let prevMonth = month - 1;
      let prevYear = year;
      if (prevMonth === 0) {
        prevMonth = 12;
        prevYear = year - 1;
      }

      // Carregar histórico do mês selecionado e do mês anterior em paralelo
      const [currentMonthHistory, previousMonthHistory] = await Promise.all([
        apiService.getDailySalesHistory(user.id, month, year),
        apiService.getDailySalesHistory(user.id, prevMonth, prevYear),
      ]);

      // Combinar os dois históricos (mês atual + mês anterior)
      const combinedHistory = [...currentMonthHistory, ...previousMonthHistory];
      setHistory(combinedHistory);

      // Filtrar e ordenar apenas o mês selecionado para exibição na tabela
      // Ordenar por data (mais recente primeiro) - manter date como string
      const filtered = currentMonthHistory
        .sort((a: DailyHistory, b: DailyHistory) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return dateB - dateA; // Mais recente primeiro
        });
      
      setFilteredHistory(filtered);

      // Calcular estatísticas do mês selecionado
      const currentMonthStats = calculateMonthlyStats(combinedHistory, month, year);
      setMonthlyStats(currentMonthStats);

      // Calcular estatísticas do mês anterior
      const prevMonthStats = calculateMonthlyStats(combinedHistory, prevMonth, prevYear);
      setPreviousMonthStats(prevMonthStats);
    } catch (error: any) {
      console.error('Erro ao carregar histórico:', error);
      showSnackbar('Erro ao carregar histórico do mês selecionado', 'error');
      // Limpar estados em caso de erro
      setFilteredHistory([]);
      setMonthlyStats(null);
      setPreviousMonthStats(null);
    }
  }, [user, calculateMonthlyStats]);

  // Carregar histórico quando mudar mês/ano
  React.useEffect(() => {
    loadHistoryForMonth(selectedMonth, selectedYear);
  }, [selectedMonth, selectedYear, loadHistoryForMonth]);

  // Carregar vendas e estatísticas
  const loadData = React.useCallback(async (date?: string) => {
    if (!user) return;

    try {
      setLoading(true);
      
      const targetDate = date || selectedDate;

      const [salesData, statsData] = await Promise.all([
        // Buscar vendas do dia (passar strings YYYY-MM-DD em Brasília)
        apiService.getAllSales({
          sellerId: user.id,
          startDate: targetDate,
          endDate: targetDate,
        }),
        apiService.getSellerStats(user.id),
      ]);

      setSales(salesData);
      setStats(statsData);
      // Histórico é carregado separadamente pela função loadHistoryForMonth
    } catch (error: any) {
      showSnackbar('Erro ao carregar dados', 'error');
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [user, selectedDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Adicionar venda
  const handleAddSale = async () => {
    if (!user) return;

    try {
      if (
        !newSale.clientPhone ||
        !newSale.freightValue ||
        !newSale.materialValue ||
        !newSale.saleDate ||
        newSale.isFirstSale === ''
      ) {
        showSnackbar('Preencha todos os campos obrigatórios', 'warning');
        return;
      }

      await apiService.createSale({
        sellerId: user.id,
        clientPhone: newSale.clientPhone,
        freightValue: parseFloat(newSale.freightValue),
        materialValue: parseFloat(newSale.materialValue),
        saleDate: newSale.saleDate,
        isFirstSale: newSale.isFirstSale === 'true',
        observations: newSale.observations,
      });

      showSnackbar('Venda registrada com sucesso! 🎉', 'success');
      setOpenAddDialog(false);
      setNewSale({
        clientPhone: '',
        freightValue: '',
        materialValue: '',
        saleDate: selectedDate,
        isFirstSale: '',
        observations: '',
      });
      loadData();
    } catch (error: any) {
      showSnackbar('Erro ao registrar venda', 'error');
      console.error('Erro ao registrar venda:', error);
    }
  };

  // Editar venda
  const handleEditClick = (sale: Sale) => {
    setEditingSale(sale);
    setOpenEditDialog(true);
  };

  const handleUpdateSale = async () => {
    if (!editingSale) return;

    try {
      await apiService.updateSale(editingSale.id, {
        clientPhone: editingSale.clientPhone,
        freightValue: editingSale.freightValue,
        materialValue: editingSale.materialValue,
        saleDate: editingSale.saleDate,
        isFirstSale: editingSale.isFirstSale,
        observations: editingSale.observations,
      });

      showSnackbar('Venda atualizada com sucesso!', 'success');
      setOpenEditDialog(false);
      setEditingSale(null);
      loadData();
    } catch (error: any) {
      showSnackbar('Erro ao atualizar venda', 'error');
      console.error('Erro ao atualizar venda:', error);
    }
  };

  // Deletar venda
  const handleDeleteSale = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta venda?')) return;

    try {
      await apiService.deleteSale(id);
      showSnackbar('Venda excluída com sucesso', 'success');
      loadData();
    } catch (error: any) {
      showSnackbar('Erro ao excluir venda', 'error');
      console.error('Erro ao excluir venda:', error);
    }
  };

  // Finalizar dia
  const handleFinalizeDay = async () => {
    if (!user) return;

    try {
      const result = await apiService.finalizeDay(user.id, selectedDate);
      showSnackbar(`Dia finalizado! ${result.history.goalMessage}`, 'success');
      setOpenFinalizeDialog(false);
      loadData();
    } catch (error: any) {
      showSnackbar('Erro ao finalizar dia', 'error');
      console.error('Erro ao finalizar dia:', error);
    }
  };

  // Voltar para hoje
  const handleGoToToday = () => {
    setSelectedDate(getTodayBrasilia());
  };

  // Formatar moeda
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Formatar data (usando helper que respeita timezone de Brasília)
  const formatDate = (date: string) => {
    return formatDateBR(date);
  };

  // Cores por status
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'GOOD':
        return '#10b981';
      case 'BASIC':
        return '#f59e0b';
      case 'BELOW_MINIMUM':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress sx={{ color: '#D4AF37' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
          Minhas Vendas
        </Typography>
        <Typography variant="body1" sx={{ color: '#6b7280' }}>
          Gerencie suas vendas e acompanhe suas metas diárias e mensais
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="Vendas do Dia" icon={<TodayIcon />} iconPosition="start" />
          <Tab label="Histórico" icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab: Vendas do Dia */}
      {currentTab === 0 && (
        <>
          {/* Cards de Metas */}
          {stats && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Meta do Dia */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: `linear-gradient(135deg, ${stats.daily.color}15 0%, ${stats.daily.color}05 100%)`,
                    border: `2px solid ${stats.daily.color}`,
                    borderRadius: 3,
                    position: 'relative',
                    overflow: 'visible',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                        Meta do Dia
                      </Typography>
                      {stats.daily.status === 'GOOD' && <CheckCircleIcon sx={{ color: stats.daily.color, fontSize: 32 }} />}
                      {stats.daily.status === 'BASIC' && <WarningIcon sx={{ color: stats.daily.color, fontSize: 32 }} />}
                      {stats.daily.status === 'BELOW_MINIMUM' && <CancelIcon sx={{ color: stats.daily.color, fontSize: 32 }} />}
                    </Box>

                    <Typography variant="h3" sx={{ fontWeight: 700, color: stats.daily.color, mb: 1 }}>
                      {formatCurrency(stats.daily.total)}
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                      {stats.daily.count} venda(s) hoje
                    </Typography>

                    <Chip
                      label={stats.daily.message}
                      sx={{
                        backgroundColor: stats.daily.color,
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    />

                    <Divider sx={{ my: 2 }} />

                    <Box>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>
                        Metas Diárias:
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#10b981', display: 'block' }}>
                        🟢 Boa meta: R$ 5.000,00
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgb(69, 165, 194)', display: 'block' }}>
                        🔵 Meta básica: R$ 3.000,00
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#f59e0b', display: 'block' }}>
                        🟠 Mínima: R$ 2.500,00
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Meta Mensal */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, #D4AF3715 0%, #D4AF3705 100%)',
                    border: '2px solid #D4AF37',
                    borderRadius: 3,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                        Meta Mensal
                      </Typography>
                      <TrendingUpIcon sx={{ color: '#D4AF37', fontSize: 32 }} />
                    </Box>

                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#D4AF37', mb: 1 }}>
                      {formatCurrency(stats.monthly.total)}
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                      {stats.monthly.count} venda(s) no mês
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600, mb: 2 }}>
                      Bonificação: {formatCurrency(stats.monthly.bonus ?? 0)}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          Progresso
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>
                          {stats.monthly.progress.toFixed(1)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(stats.monthly.progress, 100)}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: '#f3f4f6',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#D4AF37',
                            borderRadius: 5,
                          },
                        }}
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Meta: {formatCurrency(stats.monthly.goal)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: stats.monthly.remaining > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>
                      {stats.monthly.remaining > 0 
                        ? `Faltam ${formatCurrency(stats.monthly.remaining)}`
                        : '✅ Meta atingida!'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Comparativo */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: stats.comparison.percentage >= 0 
                      ? 'linear-gradient(135deg, #10b98115 0%, #10b98105 100%)'
                      : 'linear-gradient(135deg, #ef444415 0%, #ef444405 100%)',
                    border: `2px solid ${stats.comparison.percentage >= 0 ? '#10b981' : '#ef4444'}`,
                    borderRadius: 3,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                        Comparativo
                      </Typography>
                      {stats.comparison.percentage >= 0 ? (
                        <TrendingUpIcon sx={{ color: '#10b981', fontSize: 32 }} />
                      ) : (
                        <TrendingDownIcon sx={{ color: '#ef4444', fontSize: 32 }} />
                      )}
                    </Box>

                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 700, 
                        color: stats.comparison.percentage >= 0 ? '#10b981' : '#ef4444',
                        mb: 1 
                      }}
                    >
                      {stats.comparison.percentage >= 0 ? '+' : ''}{stats.comparison.percentage.toFixed(1)}%
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                      vs. mês anterior
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Mês anterior: {formatCurrency(stats.comparison.lastMonthTotal)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                      Mês atual: {formatCurrency(stats.monthly.total)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Filtro de Data e Ações */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Data"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<TodayIcon />}
                    onClick={handleGoToToday}
                    sx={{
                      borderColor: '#D4AF37',
                      color: '#D4AF37',
                      height: '56px',
                      '&:hover': {
                        borderColor: '#B8941F',
                        backgroundColor: 'rgba(212, 175, 55, 0.04)',
                      },
                    }}
                  >
                    Hoje
                  </Button>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => loadData()}
                    sx={{
                      borderColor: '#D4AF37',
                      color: '#D4AF37',
                      height: '56px',
                      '&:hover': {
                        borderColor: '#B8941F',
                        backgroundColor: 'rgba(212, 175, 55, 0.04)',
                      },
                    }}
                  >
                    Atualizar
                  </Button>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setNewSale({
                        clientPhone: '',
                        freightValue: '',
                        materialValue: '',
                        saleDate: selectedDate,
                        isFirstSale: '',
                        observations: '',
                      });
                      setOpenAddDialog(true);
                    }}
                    sx={{
                      backgroundColor: '#D4AF37',
                      color: '#1a1a1a',
                      fontWeight: 600,
                      height: '56px',
                      '&:hover': {
                        backgroundColor: '#B8941F',
                      },
                    }}
                  >
                    Nova Venda
                  </Button>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<FinalizeIcon />}
                    onClick={() => setOpenFinalizeDialog(true)}
                    disabled={sales.length === 0}
                    sx={{
                      backgroundColor: '#10b981',
                      color: '#fff',
                      fontWeight: 600,
                      height: '56px',
                      '&:hover': {
                        backgroundColor: '#059669',
                      },
                      '&:disabled': {
                        backgroundColor: '#d1d5db',
                        color: '#9ca3af',
                      },
                    }}
                  >
                    Finalizar Dia
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Tabela de Vendas */}
          <Card sx={{ borderRadius: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(212, 175, 55, 0.05)' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>DATA</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>TELEFONE</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>FRETE</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>MATERIAL</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>TOTAL</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>OBS</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', textAlign: 'center' }}>AÇÕES</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sales.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          Nenhuma venda registrada nesta data. Clique em "Nova Venda" para começar!
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sales.map((sale) => (
                      <TableRow key={sale.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                            <Typography variant="body2">{formatDate(sale.saleDate)}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                            <Typography variant="body2">{sale.clientPhone}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ShippingIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                            <Typography variant="body2" sx={{ color: '#3b82f6', fontWeight: 500 }}>
                              {formatCurrency(sale.freightValue)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MaterialIcon sx={{ fontSize: 18, color: '#10b981' }} />
                            <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600 }}>
                              {formatCurrency(sale.materialValue)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(sale.totalValue)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={sale.observations || 'Sem observações'}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                maxWidth: 150, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap' 
                              }}
                            >
                              {sale.observations || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(sale)}
                            sx={{ color: '#3b82f6', mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSale(sale.id)}
                            sx={{ color: '#ef4444' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      {/* Tab: Histórico Mensal */}
      {currentTab === 1 && (
        <>
          {/* Filtros de Mês/Ano */}
          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <DateRangeIcon sx={{ color: '#D4AF37' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mr: 2 }}>
                  Filtrar por Período
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Mês</InputLabel>
                  <Select
                    value={selectedMonth}
                    label="Mês"
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  >
                    {monthNames.map((name, index) => (
                      <MenuItem key={index} value={index + 1}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Ano</InputLabel>
                  <Select
                    value={selectedYear}
                    label="Ano"
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                  >
                    {[2024, 2025, 2026].map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>

          {/* Cards de Resumo Mensal e Comparação */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Resumo do Mês Selecionado */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, border: '2px solid #D4AF37', height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrophyIcon sx={{ color: '#D4AF37' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {monthNames[selectedMonth - 1]} {selectedYear}
                    </Typography>
                  </Box>
                  
                  {monthlyStats ? (
                    <>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: '#D4AF37', mb: 2 }}>
                        {formatCurrency(monthlyStats.totalSales)}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 1.5, backgroundColor: '#f8fafc', textAlign: 'center' }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981' }}>
                              {monthlyStats.salesCount}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              Vendas no mês
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 1.5, backgroundColor: '#f8fafc', textAlign: 'center' }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                              {formatCurrency(monthlyStats.avgDailySales)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              Média diária
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      {/* Metas atingidas */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={`${monthlyStats.daysWithGoodGoal} dias boa meta`}
                          size="small"
                          sx={{ backgroundColor: '#10b981', color: '#fff' }}
                        />
                        <Chip
                          icon={<WarningIcon />}
                          label={`${monthlyStats.daysWithBasicGoal} dias básica`}
                          size="small"
                          sx={{ backgroundColor: '#f59e0b', color: '#fff' }}
                        />
                        <Chip
                          icon={<CancelIcon />}
                          label={`${monthlyStats.daysWithBelowGoal} dias abaixo`}
                          size="small"
                          sx={{ backgroundColor: '#ef4444', color: '#fff' }}
                        />
                      </Box>

                      {monthlyStats.bestDay && (
                        <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#10b98115', borderRadius: 2, border: '1px solid #10b981' }}>
                          <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600 }}>
                            ⭐ Melhor dia: {formatDate(monthlyStats.bestDay.date)} - {formatCurrency(monthlyStats.bestDay.total)}
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        Nenhum dado para este mês
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Comparação com Mês Anterior */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, border: '2px solid #6b7280', height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CompareIcon sx={{ color: '#6b7280' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Comparação com {monthNames[selectedMonth === 1 ? 11 : selectedMonth - 2]} {selectedMonth === 1 ? selectedYear - 1 : selectedYear}
                    </Typography>
                  </Box>

                  {monthlyStats && previousMonthStats ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        {monthlyStats.totalSales >= previousMonthStats.totalSales ? (
                          <TrendingUpIcon sx={{ fontSize: 48, color: '#10b981' }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 48, color: '#ef4444' }} />
                        )}
                        <Box>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontWeight: 700, 
                              color: monthlyStats.totalSales >= previousMonthStats.totalSales ? '#10b981' : '#ef4444' 
                            }}
                          >
                            {monthlyStats.totalSales >= previousMonthStats.totalSales ? '+' : ''}
                            {((monthlyStats.totalSales - previousMonthStats.totalSales) / previousMonthStats.totalSales * 100).toFixed(1)}%
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            em relação ao mês anterior
                          </Typography>
                        </Box>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 1.5, backgroundColor: '#f8fafc' }}>
                            <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                              Mês Anterior
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#6b7280' }}>
                              {formatCurrency(previousMonthStats.totalSales)}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 1.5, backgroundColor: '#D4AF3715' }}>
                            <Typography variant="caption" sx={{ color: '#D4AF37', display: 'block' }}>
                              Mês Atual
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#D4AF37' }}>
                              {formatCurrency(monthlyStats.totalSales)}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Vendas Anterior</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{previousMonthStats.salesCount}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Vendas Atual</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{monthlyStats.salesCount}</Typography>
                        </Box>
                      </Box>
                    </>
                  ) : previousMonthStats ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                        Mês anterior: {formatCurrency(previousMonthStats.totalSales)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                        Sem dados do mês atual para comparar
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        Sem dados para comparação
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabela de Histórico Diário do Mês */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Histórico Diário - {monthNames[selectedMonth - 1]} {selectedYear}
              </Typography>

              {filteredHistory.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <HistoryIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
                  <Typography variant="body1" sx={{ color: '#6b7280' }}>
                    Nenhum histórico encontrado para {monthNames[selectedMonth - 1]} {selectedYear}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                    Finalize um dia para começar a criar seu histórico
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'rgba(212, 175, 55, 0.05)' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>DATA</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>TOTAL VENDIDO</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Nº VENDAS</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>META ATINGIDA</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>STATUS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredHistory.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatDate(item.date)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                              {formatCurrency(Number(item.totalSales))}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={`${item.salesCount} vendas`} 
                              size="small"
                              sx={{ 
                                backgroundColor: '#e5e7eb',
                                color: '#1f2937',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              {item.goalMessage}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                item.goalStatus === 'GOOD' ? 'Boa Meta' :
                                item.goalStatus === 'BASIC' ? 'Meta Básica' :
                                'Abaixo da Meta'
                              }
                              sx={{
                                backgroundColor: getStatusColor(item.goalStatus),
                                color: '#fff',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Dialog: Adicionar Venda */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#1a1a1a', fontWeight: 600 }}>
          Nova Venda
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Telefone do Cliente"
                value={newSale.clientPhone}
                onChange={(e) => setNewSale({ ...newSale, clientPhone: e.target.value })}
                placeholder="(00) 00000-0000"
                required
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: '#6b7280' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valor do Frete"
                type="number"
                value={newSale.freightValue}
                onChange={(e) => setNewSale({ ...newSale, freightValue: e.target.value })}
                placeholder="0.00"
                required
                InputProps={{
                  startAdornment: <ShippingIcon sx={{ mr: 1, color: '#3b82f6' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valor do Material"
                type="number"
                value={newSale.materialValue}
                onChange={(e) => setNewSale({ ...newSale, materialValue: e.target.value })}
                placeholder="0.00"
                required
                InputProps={{
                  startAdornment: <MaterialIcon sx={{ mr: 1, color: '#10b981' }} />,
                }}
                helperText="Conta para a meta"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Data da Venda"
                type="date"
                value={newSale.saleDate}
                onChange={(e) => setNewSale({ ...newSale, saleDate: e.target.value })}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Primeira venda?</InputLabel>
                <Select
                  value={newSale.isFirstSale}
                  label="Primeira venda?"
                  onChange={(e) => setNewSale({ ...newSale, isFirstSale: e.target.value })}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  <MenuItem value="true">Sim</MenuItem>
                  <MenuItem value="false">Não</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                value={newSale.observations}
                onChange={(e) => setNewSale({ ...newSale, observations: e.target.value })}
                multiline
                rows={3}
                placeholder="Informações adicionais sobre a venda..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenAddDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAddSale}
            sx={{
              backgroundColor: '#D4AF37',
              color: '#1a1a1a',
              '&:hover': { backgroundColor: '#B8941F' },
            }}
          >
            Salvar Venda
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Editar Venda */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#1a1a1a', fontWeight: 600 }}>
          Editar Venda
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {editingSale && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Telefone do Cliente"
                  value={editingSale.clientPhone}
                  onChange={(e) => setEditingSale({ ...editingSale, clientPhone: e.target.value })}
                  required
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: '#6b7280' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Valor do Frete"
                  type="number"
                  value={editingSale.freightValue}
                  onChange={(e) => setEditingSale({ ...editingSale, freightValue: parseFloat(e.target.value) })}
                  required
                  InputProps={{
                    startAdornment: <ShippingIcon sx={{ mr: 1, color: '#3b82f6' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Valor do Material"
                  type="number"
                  value={editingSale.materialValue}
                  onChange={(e) => setEditingSale({ ...editingSale, materialValue: parseFloat(e.target.value) })}
                  required
                  InputProps={{
                    startAdornment: <MaterialIcon sx={{ mr: 1, color: '#10b981' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Data da Venda"
                  type="date"
                  value={editingSale.saleDate.split('T')[0]}
                  onChange={(e) => setEditingSale({ ...editingSale, saleDate: e.target.value })}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Primeira venda?</InputLabel>
                  <Select
                    value={editingSale.isFirstSale ? 'true' : 'false'}
                    label="Primeira venda?"
                    onChange={(e) =>
                      setEditingSale({ ...editingSale, isFirstSale: e.target.value === 'true' })
                    }
                  >
                    <MenuItem value="true">Sim</MenuItem>
                    <MenuItem value="false">Não</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observações"
                  value={editingSale.observations || ''}
                  onChange={(e) => setEditingSale({ ...editingSale, observations: e.target.value })}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleUpdateSale}
            sx={{
              backgroundColor: '#D4AF37',
              color: '#1a1a1a',
              '&:hover': { backgroundColor: '#B8941F' },
            }}
          >
            Atualizar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Finalizar Dia */}
      <Dialog open={openFinalizeDialog} onClose={() => setOpenFinalizeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#10b981', color: '#fff', fontWeight: 600 }}>
          Finalizar Dia
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <FinalizeIcon sx={{ fontSize: 64, color: '#10b981', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Deseja finalizar o dia {formatDate(selectedDate)}?
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
              Ao finalizar, o histórico será salvo e você poderá começar um novo dia.
            </Typography>

            {stats && (
              <Box sx={{ 
                backgroundColor: 'rgba(212, 175, 55, 0.05)', 
                borderRadius: 2, 
                p: 2,
                border: '1px solid rgba(212, 175, 55, 0.2)'
              }}>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                  Resumo do Dia:
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#D4AF37', mb: 1 }}>
                  {formatCurrency(stats.daily.total)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  {stats.daily.count} venda(s) • {stats.daily.message}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenFinalizeDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleFinalizeDay}
            sx={{
              backgroundColor: '#10b981',
              color: '#fff',
              '&:hover': { backgroundColor: '#059669' },
            }}
          >
            Confirmar Finalização
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SellerSales;
